import '../styles/review_widget.scss';
import EmblaCarousel from './embla-carousel.js';
// @ts-ignore
import googleLogoSquare from '../assets/google-logo-square.png';
// @ts-ignore
import buennoLogo from '../assets/buenno-logo.png';

let widgetLocale  = /*widgetData?.locale ||*/ 'en';

export const SUPPORTED_LOCALES = ['en', 'fi'] as const;
export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

/**
 * Font weights to load. This should use all weights used in stylesheet.
 * Currently not set per font.
 */
const loadFontWeights = [400, 500, 600];

/**
 * Fonts to load in addition to user-defined fonts. This should use all fonts used in stylesheet.
 */
const defaultFontFamilies = ['Rubik'];

/**
 * Review data structure.
 */
type ReviewData = {
  reviewer: string;
  answer: string;
  score: number;
  date: string;
  avatar?: string;
};

function prepareReviewData(rawReviewData: any): ReviewData {
  return {
    reviewer: rawReviewData.name || "",
    answer: rawReviewData.open_comment || "",
    score: rawReviewData.answer_value,
    date: rawReviewData.created_at,
    avatar: undefined, // or apiReview.avatar if available
  };
}

/**
 * Options for creating a review card.
 */
type CreateReviewCardOptions = {
  cardClass?: string;
  truncate?: boolean;
  onReadMore?: () => void;
};

function extractFonts(settings: any): { name: string; type: string }[] {
  const fonts = settings?.fonts ?? {};
  return Object.values(fonts)
      .filter((font: any) => font && typeof font === 'object' && font.name && font.type) as { name: string; type: string }[];
}

function extractColors(settings: any): Record<string, string> {
  return settings?.colors ?? {};
}

function applyUserDefinedStyles(container: HTMLElement, settings: any) {
  const colors = extractColors(settings);
  const fonts = extractFonts(settings);

  Object.entries(colors).forEach(([key, value]) => {

    // If shadow or border color, format as rgb.
    if (/_shadow_color$|_border_color$/.test(key)) {
      const rgb = hexToRgb(value);

      container.style.setProperty(`--${key}`, rgb || value);
    } else {
      container.style.setProperty(`--${key}`, value);
    }
  });

  const fontVarNames = [
    'user_defined_header_rating_font_family',
    'user_defined_header_total_reviews_font_family',
    'user_defined_review_button_font_family',
    'user_defined_author_font_family',
    'user_defined_review_date_font_family',
    'user_defined_review_body_font_family',
    'user_defined_read_more_button_font_family',
    'user_defined_author_initial_font_family',
    'user_defined_global_font_family'
  ];

  const globalFont = fonts[0] || { name: 'Rubik', type: 'Google' };

  fontVarNames.forEach(fontVarName => {
    container.style.setProperty(`--${fontVarName}`, `"${globalFont.name}"`);
  });

  loadFonts(fonts);
}

function hexToRgb(hex: string | undefined | null): string | null {
  if (!hex) return null;
  let _hex = hex.replace('#', '').toLowerCase();
  if (_hex.length === 3) _hex = _hex.split('').map(x => x + x).join('');
  if (_hex.length !== 6) return null;

  const num = parseInt(_hex, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;

  return `${r}, ${g}, ${b}`;
}

/**
 * Returns "x y ago", where x is a number and y is day/month/year etc
 */
function formatDateTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now  = new Date();
  const diff = now.getTime() - date.getTime();

  // const seconds = Math.floor(diff / 1_000);
  // const minutes = Math.floor(seconds / 60);
  // const rawHours = minutes / 60;

  const rawHours = diff / (1000 * 60 * 60);
  const hours   = Math.ceil(rawHours);
  const days    = Math.floor(hours   / 24);
  const weeks   = Math.floor(days    / 7);
  const months  = Math.floor(days    / 30);
  const years   = Math.floor(months  / 12);

  const rtf = new Intl.RelativeTimeFormat(widgetLocale, { numeric: 'auto' });

  if (rawHours < 1) {
    return rtf.format(-1, 'hour');
  }
  if (hours < 24) {
    return rtf.format(-hours, 'hour');
  }
  if (days < 7) {
    return rtf.format(-days, 'day');
  }
  if (weeks < 4) {
    return rtf.format(-weeks, 'week');
  }
  if (months < 12) {
    return rtf.format(-months, 'month');
  }

  // If 12 months or more ago -> years ago
  return rtf.format(-years, 'year');
}

/**
 * Get a review card for use in both
 * the list-view and modal (single review) view.
 */
function createReviewCard(review: ReviewData, opts: CreateReviewCardOptions = {}): HTMLDivElement {
  const { cardClass, truncate = false, onReadMore } = opts;

  const reviewCard = document.createElement('div');
  reviewCard.className = cardClass || 'buenno__review-card';

  const cardHeader = document.createElement('div');
  cardHeader.className = 'buenno__card-header';
  cardHeader.innerHTML = `
    <div class="buenno__rating-date-block">
      <div class="buenno__rating-stars">
        ${getStarsForScore(review.score, true)}
      </div>
      <span class="buenno__review-date">${formatDateTime(review.date)}</span>
    </div>
    <img loading="lazy" src="${googleLogoSquare}" alt="Google" class="buenno__google-logo" />
  `;
  reviewCard.appendChild(cardHeader);

  const cardBody = document.createElement('div');
  cardBody.className = 'buenno__card-body';

  const reviewText = document.createElement('p');
  reviewText.className = 'buenno__review-text';
  reviewText.textContent = review.answer;
  reviewText.setAttribute('data-full-text', review.answer);

  cardBody.appendChild(reviewText);
  reviewCard.appendChild(cardBody);

  if (truncate) {
    requestAnimationFrame(() => {
      handleTruncation(reviewText, true);
    });

    const readMoreButtonContainer = document.createElement('div');
    readMoreButtonContainer.className = 'buenno__read-more-button-container';

    const readMoreButton = document.createElement('a');
    readMoreButton.className = 'buenno__read-more-button';
    readMoreButton.textContent = 'Read more';
    readMoreButton.href = '#';

    readMoreButtonContainer.appendChild(readMoreButton);
    reviewCard.appendChild(readMoreButtonContainer);

    readMoreButton.addEventListener('click', (evt) => {
      evt.preventDefault();
      if (onReadMore) onReadMore();
    });
  }

  const cardFooter = document.createElement('div');
  cardFooter.className = 'buenno__card-footer';

  let avatarHtml = '';
  if (review.avatar) {
    avatarHtml = `<img loading="lazy" class="buenno__reviewer-picture" src="${review.avatar}" />`;
  } else {
    avatarHtml = `
      <div class="buenno__reviewer-initial" aria-label="${review.reviewer}">
        ${review.reviewer.charAt(0).toUpperCase()}
      </div>`;
  }
  cardFooter.innerHTML = `
    ${avatarHtml}
    <span class="buenno__reviewer-name">${review.reviewer || "Anonymous"}</span>
  `;
  reviewCard.appendChild(cardFooter);

  return reviewCard;
}

/**
 * Truncate text word-by-word from an element until it fits
 * in the container.
 */
function truncateToFit(textElement: HTMLElement): boolean {
  const container = textElement.parentElement as HTMLElement;
  if (!container) return false;

  if (textElement.scrollHeight <= container.clientHeight) {
    return false;
  }

  let truncated = textElement.textContent || "";

  while (textElement.scrollHeight > container.clientHeight) {
    const lastSpace = truncated.lastIndexOf(" ");
    if (lastSpace === -1) {
      truncated = "";
      break;
    }
    truncated = truncated.slice(0, lastSpace);
    textElement.textContent = truncated + "...";
  }

  return true;
}

/**
 * Truncate text in an element and add a "Read more" link
 * if the text is truncated.
 */
function handleTruncation(textElement: HTMLElement, isInitial: boolean) {
  if (isInitial) {
    textElement.setAttribute("data-full-text", textElement.textContent || "");
  } else {
    textElement.textContent = textElement.getAttribute('data-full-text') || "";
  }

  const didTruncate = truncateToFit(textElement);
  const reviewCard = textElement.parentElement?.parentElement as HTMLElement;
  const buttonContainer = reviewCard.querySelector('.buenno__read-more-button-container') as HTMLElement;
  if (!buttonContainer) return;

  if (didTruncate) {
    buttonContainer.classList.remove('buenno__is-hidden');
  } else {
    buttonContainer.classList.add('buenno__is-hidden');
  }
}

/**
 * Get a localized string for given key.
 */
function getStringForKey(key: string): string {
  const strings = (window as any).localizedStrings;
  if (!strings) return key;

  const localeStrings = strings[widgetLocale] || {};
  return localeStrings[key] || key;
}

/**
 * Include loads for given fonts, but only those of type Google.
 */
function loadFonts(fonts: { name: string; type: string }[] = []) {
  const mergedFonts = [...fonts, ...defaultFontFamilies.map(name => ({ name, type: 'Google' }))];
  const uniqueFonts = Object.values(
    mergedFonts.reduce((acc, font) => {
      acc[font.name] = font;
      return acc;
    }, {} as Record<string, { name: string; type: string }>)
  );

  uniqueFonts
    .filter((font) => font.type === 'Google')
    .forEach((font) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${font.name.replace(/ /g, '+')}:wght@${loadFontWeights.join(';')}&display=swap`;
      document.head.appendChild(link);
    });
}

/**
 * Get SVG for a star icon. Fill color is changeable in CSS.
 */
function getStarSvg(withClass: string): string {
  return `
    <svg width="17" height="16" viewBox="0 0 17 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" class="buenno__star-icon ${withClass}">
      <path d="M8.5 0L10.8982 5.19921L16.584 5.87336L12.3803 9.76079L13.4962 15.3766L8.5 12.58L3.50383 15.3766L4.61969 9.76079L0.416019 5.87336L6.10184 5.19921L8.5 0Z" />
    </svg>
  `;
}

function getCrossSvg(): string {
  return `
    <svg width="17" height="17" viewBox="0 0 17 17" fill="transparent" xmlns="http://www.w3.org/2000/svg" class="buenno__close-icon">
      <path d="M2 2L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      <path d="M15 2L2 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
    </svg>
  `;
}

/**
 * Get SVG for an arrow icon. Direction can be specified and Stroke color is changeable in CSS:
 */
function getArrowSvg(direction: string): string {
  if (direction == 'left') {
    return `
      <svg width="17" height="17" viewBox="0 0 17 17" fill="transparent" xmlns="http://www.w3.org/2000/svg" class="buenno__arrow-icon">
        <path d="M12.0417 1.41667L4.95832 8.5L12.0417 15.5833" stroke="currentColor" stroke-width="2"/>
      </svg>
    `;
  } else if (direction == 'right') {
    return `
      <svg width="17" height="17" viewBox="0 0 17 17" fill="transparent" xmlns="http://www.w3.org/2000/svg" class="buenno__arrow-icon">
        <path d="M4.95834 1.41667L12.0417 8.5L4.95834 15.5833" stroke="currentColor" stroke-width="2"/>
      </svg>
    `;
  }

  return '';
}

/**
 * Get stars for given score.
 * Filled stars are included. Empty too, if requested.
 */
function getStarsForScore(score: number, withEmpty: boolean = false): string {
  const filled = Math.max(0, Math.round(score));
  const empty = 5 - filled;

  let stars = '';

  stars += new Array(filled).fill(getStarSvg('buenno__filled')).join('');
  if (withEmpty) {
    stars += new Array(empty).fill(getStarSvg('buenno__empty')).join('');
  }

  return stars;
}

/**
 * Get footer for widget.
 */
function getFooter(): HTMLDivElement {
  const footer = document.createElement('div');
  footer.className = 'buenno__footer-container';

  footer.innerHTML = `
    <span class="buenno__powered-by">Powered by</span>
    <img loading="lazy" src="${buennoLogo}" alt="Buenno" class="buenno__buenno-logo" />
  `;

  return footer;
}

/**
 * Get header for widget.
 */
function getHeader(reviewButtonVisible: boolean, reviewButtonUrl: string, totalReviews: number, overallScore: number): HTMLDivElement {
  const header = document.createElement('div');
  header.className = 'buenno__header-container';

  const shouldShowReviewButton = reviewButtonVisible && reviewButtonUrl != null;

  const reviewButtonHtml = shouldShowReviewButton ? `
    <div class="buenno__flex-row">
      <a href=${reviewButtonUrl}
         target="_blank"
         rel="noopener"
         class="buenno__review-button">
         ${getStringForKey('button_write_review')}
      </a>
    </div>
  ` : '';

  header.innerHTML = `
    <div class="buenno__flex-row">
      <img loading="lazy" src="${googleLogoSquare}" alt="Google" class="buenno__google-logo" />
      <span class="buenno__rating">${overallScore.toFixed(1)}</span>
      <div class="buenno__rating-stars">
        ${getStarsForScore(overallScore, true)}
      </div>
    </div>
    
    <div class="buenno__flex-row">
      <span class="buenno__reviews-count">${totalReviews} ${getStringForKey('reviews_plural')}</span>
    </div>
    
    ${reviewButtonHtml}
  `;

  return header;
}

function openFullReviewModal(widgetContainer: HTMLElement, review: ReviewData) {
  const overlay = document.createElement('div');
  overlay.className = 'buenno__review-modal-overlay';

  const modal = document.createElement('div');
  modal.className = 'buenno__review-modal';

  const closeBtn = document.createElement('button');
  closeBtn.className = 'buenno__review-modal-close-button';
  closeBtn.innerHTML = getCrossSvg();

  const reviewCard = createReviewCard(review, { truncate: false });

  reviewCard.querySelector('.buenno__card-header')?.appendChild(closeBtn);
  modal.appendChild(reviewCard);
  overlay.appendChild(modal);
  widgetContainer.appendChild(overlay);

  function closeModal() {
    overlay.remove();
    document.removeEventListener('keydown', onEscKey);
  }

  closeBtn.addEventListener('click', closeModal);

  overlay.addEventListener('click', (evt) => {
    if (evt.target === overlay) {
      closeModal();
    }
  });

  function onEscKey(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      closeModal();
    }
  }
  document.addEventListener('keydown', onEscKey);
}

export function initReviewWidget(container: HTMLElement, widgetData: any, locale: string = 'en') {
  try {
    const reviewCardGap = 22;

    widgetLocale = locale || widgetData?.locale || 'en';
    const settings = widgetData?.settings || {};
    applyUserDefinedStyles(container, settings);

    const data = widgetData;
    if (!data?.answers?.length) return;

    const reviews: ReviewData[] = (data.answers || []).map(prepareReviewData);

    container.innerHTML  = '';

    const headerVisible = settings.header_visible || false;
    const reviewButtonVisible = settings.review_button_visible || false;
    const reviewButtonUrl = settings.review_button_url || null;

    // Build header + carousel wrapper
    if (headerVisible) {
      container.appendChild(getHeader(reviewButtonVisible, reviewButtonUrl, data.total_answer_count, data.overall_score));
    }
    const emblaNode = document.createElement('div');
    emblaNode.className = 'embla';

    // Prev button
    const prevBtn = document.createElement('button');
    prevBtn.className = 'embla__prev';
    prevBtn.innerHTML = getArrowSvg('left');
    emblaNode.append(prevBtn);

    // Viewport + slides container
    const viewport = document.createElement('div');
    viewport.className = 'embla__viewport';

    const emblaContainer = document.createElement('div');
    emblaContainer.className = 'embla__container';

    // append all slides
    reviews.forEach((rev: ReviewData) => {
      emblaContainer.appendChild(createReviewCard(rev, {
        cardClass: 'embla__slide buenno__review-card',
        truncate: true,
        onReadMore: () => openFullReviewModal(container, rev),
      }));
    });
    viewport.append(emblaContainer);
    emblaNode.append(viewport);

    // Next button
    const nextBtn = document.createElement('button');
    nextBtn.className = 'embla__next';
    nextBtn.innerHTML = getArrowSvg('right');
    emblaNode.append(nextBtn);

    container.append(emblaNode, getFooter());

    const emblaApi = EmblaCarousel(viewport, {
      loop: false, align: 'start', slidesToScroll: 'auto', dragFree: true
    });

    prevBtn.addEventListener('click', emblaApi.scrollPrev, false)
    nextBtn.addEventListener('click', emblaApi.scrollNext, false)

    function handleResize() {
      // Container's width, gap and button widths.
      const containerW = container.getBoundingClientRect().width
      const navGap = parseInt(getComputedStyle(emblaNode).gap) || reviewCardGap
      const prevW = prevBtn.getBoundingClientRect().width
      const nextW = nextBtn.getBoundingClientRect().width

      // Total available width for review cards.
      const emblaContainerWidth = containerW - prevW - nextW - navGap * 2
      const emblaContainerGap = parseInt(getComputedStyle(emblaContainer).gap) || reviewCardGap

      // Calculate max cards that will fit.
      const minCardWidth = 220
      let count = Math.floor((emblaContainerWidth + emblaContainerGap) / (minCardWidth + emblaContainerGap))
      count = Math.max(1, Math.min(count, data.answers.length))

      // Calculate card exact width.
      const totalGaps = (count - 1) * emblaContainerGap
      const cardWidth = (emblaContainerWidth - totalGaps) / count

      // Apply new card width.
      emblaContainer.querySelectorAll<HTMLDivElement>('.embla__slide').forEach(slide => {
        slide.style.flex  = `0 0 ${cardWidth}px`;
        slide.style.width = `${cardWidth}px`;
      })
    }

    // Set up resize observer for truncation.
    let resizeObserverDebounceTimer: number|null = null;
    const resizeObserver = new ResizeObserver(entries => {
      if (resizeObserverDebounceTimer !== null) {
        clearTimeout(resizeObserverDebounceTimer);
      }

      resizeObserverDebounceTimer = window.setTimeout(() => {
        entries.forEach(entry => {
          const cardBody   = entry.target as HTMLElement;
          const reviewText = cardBody.querySelector('.buenno__review-text') as HTMLElement;
          handleTruncation(reviewText, false);
        });

        resizeObserverDebounceTimer = null;
      }, 100);
    });

    emblaContainer
      .querySelectorAll<HTMLElement>('.buenno__card-body')
      .forEach(body => resizeObserver.observe(body));

    emblaApi.on('resize', handleResize);
    window.addEventListener('resize', handleResize);
    handleResize();

  } catch (err) {
    console.error('Widget init failed:', err);
  }
}